import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { AuthService } from './auth.service';
import { UiService } from '../shared/services/ui.service';
import { StorageConst } from '../shared/consts/storage-consts';
import { SalesMonitorGuard } from '../main/sales-monitor/sales-monitor.guard';

/**
 * Responsible for showing the login page instead of requested pages in case
 * the user in not logged in, and on the counter event,
 * navigate away from the login page in case the user is logged in.
 */
@Injectable()
export class AuthGuard implements CanActivate, CanActivateChild {
  private guestEndpoints = [
    '/session/signin',
    '/session/signup',
    '/session/forgot',
    '/session/signin?posId={posId}',
    '/session/signin?accessCode={accessCode}',
    '/session/signin?posId={posId}&accessCode={accessCode}&isRedirectedFromPOS={isRedirectedFromPOS}&adminToken={adminToken}',
  ];
  posId;
  accessCode;
  isRedirectedFromPOS;
  adminToken;

  constructor(
    private authService: AuthService,
    private router: Router,
    private uiService: UiService,
    private saleMonitorGuard: SalesMonitorGuard) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> | UrlTree {
    const url: string = state.url;
    const isBetaRedirect = route.queryParams['autoLoginToken'] && route.queryParams['posId'];
    const isAutoLogin = route.queryParams['x'] && route.queryParams['y'] && route.queryParams['accessCode'];
    // if (route.routeConfig.path === 'session') {
    //   console.log(route.routeConfig.path)
    //   if (isBetaRedirect) {
    //     return this.router.createUrlTree([this.authService.redirectUrl], {queryParams: route.queryParams});
    //   }
    //   return Promise.resolve(true);
    // }

    try {
      if (isAutoLogin) {
        sessionStorage.setItem(StorageConst.X, route.queryParams['x']);
        sessionStorage.setItem(StorageConst.Y, route.queryParams['y']);
        sessionStorage.setItem(StorageConst.ACCESS_CODE, this.accessCode);
        this.uiService.setAutoLogin(true)
      }

      if (route.queryParams['accessCode']) {
        this.authService.setAccessCode(route.queryParams['accessCode'])
        this.accessCode = route.queryParams['accessCode'];
        sessionStorage.setItem(StorageConst.ACCESS_CODE, this.accessCode);
      } else {
        this.accessCode = sessionStorage.getItem('accessCode');
        this.authService.setAccessCode(this.accessCode)
      }
      if (route.queryParams['posId']) {
        this.posId = route.queryParams['posId'];
        sessionStorage.setItem('defaultPosId', this.posId);
      } else {
        this.posId = sessionStorage.getItem('defaultPosId');
      }
    } catch (e) {
      this.posId = sessionStorage.getItem('defaultPosId');
      this.accessCode = sessionStorage.getItem('accessCode');
      this.authService.setAccessCode(this.accessCode)
    }

    if (route.queryParams['isRedirectedFromPOS'] && route.queryParams['adminToken']) {
      this.isRedirectedFromPOS = route.queryParams['isRedirectedFromPOS'];
      sessionStorage.setItem(StorageConst.REDIRECTED_FROM_POS, this.isRedirectedFromPOS);
      this.adminToken = route.queryParams['adminToken'];
      sessionStorage.setItem(StorageConst.TOKEN, this.adminToken);
    } else if (isBetaRedirect) {
      sessionStorage.setItem(StorageConst.TOKEN, route.queryParams['autoLoginToken']);
      sessionStorage.setItem(StorageConst.REDIRECTED_FROM_MULTI_VERSION, '1');
    }
    this.guestEndpoints[3] = this.guestEndpoints[3].replace('{posId}', this.posId);
    this.guestEndpoints[4] = this.guestEndpoints[4].replace('{accessCode}', this.accessCode);
    this.guestEndpoints[5] = `/crm/main/search?posId=${this.posId}&accessCode=${this.accessCode}&isRedirectedFromPOS=${this.isRedirectedFromPOS}&adminToken=${this.adminToken}`;
    return this.checkIsLoggedIn(url);
  }

  /**
   * Asks the server (using the auth service) whether the user is logged in or not,
   * and navigate to the appropriate page for every case (login for unauthenticated,
   * and to the index page/previously requested url in the other case).
   * @param url the requested url
   */
  checkIsLoggedIn(url: string): Promise<boolean> {
    return this.authService.isLoggedIn()
      .then(async isLoggedIn => {
        if (isLoggedIn) {
          await this.uiService.loadParameters();
          if (
            (this.guestEndpoints.indexOf(url) > -1) ||
            this.isGuestPageUrl(url) ||
            sessionStorage.getItem(StorageConst.REDIRECTED_FROM_MULTI_VERSION)
          ) {
            sessionStorage.removeItem(StorageConst.REDIRECTED_FROM_MULTI_VERSION);
            this.router.navigate([this.authService.redirectUrl]);
          }
          this.uiService.checkDirection();
          return true;
        } else {
          if (this.guestEndpoints.indexOf(url) === -1 && !this.isGuestPageUrl(url)) {
            // Store the attempted URL so we can return to it after login (keeps query params like
            // accessCode / inventoryLocationId). Never overwrite a real pending target with the bare
            // homepage '/', otherwise a later root navigation while logged-out wipes the deep link.
            if (url && url !== '/') {
              this.authService.redirectUrl = url;
            }
            sessionStorage.removeItem(StorageConst.TOKEN);
            sessionStorage.removeItem(StorageConst.USER);
            // Carry the accessCode onto the signin URL so the login form keeps it pre-filled and a
            // re-run of this guard on the signin route doesn't reset it to null.
            this.router.navigate([this.guestEndpoints[0]], this.accessCode ? {queryParams: {accessCode: this.accessCode}} : {});
            // Don't also allow the ORIGINAL navigation to proceed: returning true here let Angular
            // resolve this route's resolvers (e.g. SiteResolver) with no token yet, before the
            // redirect above took effect - poisoning them for the rest of the session (BIG-98).
            return false;
          }
          return true;
        }
      });
  }

  /**
   * True for /session/signin, /session/signup, /session/forgot regardless of extra query params.
   * The exact-match `guestEndpoints` array above breaks whenever the signin URL carries an
   * unexpected param (e.g. ngx-bp-refresh's own auto-reload appends ?reloadTime=...), which made
   * this guard treat the signin page itself as a protected "deep link" to return to after login -
   * poisoning `redirectUrl` with the signin URL so a successful login just bounced back to signin
   * (BIG-98).
   */
  private isGuestPageUrl(url: string): boolean {
    const path = url.split('?')[0];
    return ['/session/signin', '/session/signup', '/session/forgot'].indexOf(path) > -1;
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {

    /**
     * Sales monitor
     */
    const credentials = this.saleMonitorGuard.getCredentials();
    if (Object.values(credentials).every(Boolean) && !state.url.includes('/sales-monitor')) {
      return this.router.createUrlTree(['/sales-monitor'], {queryParams: credentials, queryParamsHandling: 'merge'});
    }

    return true;
  }
}
